import type React from 'react';
import { useEffect, useRef } from 'react';

import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { DeepPartial as ApolloDeepPartial } from '@apollo/client/utilities';
import type { GridApi, GridRowId, GridTreeNode, GridValidRowModel } from '@mui/x-data-grid-premium';
import { isEqual, throttle } from 'lodash';
import type {
  GroupHeaderRow,
  OrphanRelationshipRow,
  ReportGridRow,
  ReportRowQuestionJob,
  UrlRelationshipRow,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { Sentry } from 'src/sentry';
import type { DeepPartial } from 'ts-essentials';

import type { ReportDataSourceCache } from './report-data-source-cache';

/** The variables of one `reportRows` cache entry. Paging args are not part of the cache key. */
export type WatchedReportRowsVariables = Omit<
  Apollo.GetReportRowsQueryVariables,
  'limit' | 'offset'
>;

interface ReportRowsGridApi extends Pick<GridApi, 'updateRows'> {
  getRowNode: (id: GridRowId) => Pick<GridTreeNode, 'depth'> | null;
}

/** One row of a `reportRows` entry as a partial-data watcher reads it. */
type CachedReportRow = ApolloDeepPartial<
  NonNullable<Apollo.GetReportRowsQuery['report']>['reportRows']['rows'][number]
>;

interface PendingRowUpdate {
  readonly apolloRow: object;
  readonly appliedRows: Map<GridRowId, object>;
  readonly gridRow: GridValidRowModel;
}

/**
 * Records a `reportRows` cache entry a row load just wrote. A new filter or
 * sort replaces the list, since rows loaded under the old one are gone.
 */
export function addWatchedReportRowsVariables({
  isNewQuery,
  next,
  prev,
}: {
  isNewQuery: boolean;
  next: WatchedReportRowsVariables;
  prev: readonly WatchedReportRowsVariables[];
}): readonly WatchedReportRowsVariables[] {
  if (isNewQuery) return [next];
  if (prev.some(prevVariables => isEqual(prevVariables, next))) return prev;
  return [...prev, next];
}

/**
 * Watches the Apollo cache for changes to documents in every loaded rows entry.
 * When any cached Document or DocumentRelationship referenced by an entry
 * is modified (by a mutation, subscription, or cache write), its observable
 * fires and reconciles the grid against the latest cache state.
 *
 * Grouped and relationship views write one entry per expanded level, keyed by
 * `groupKeys` or `parentTreeRowId`, so each entry gets its own watcher. Only
 * root rows are patched in the grid: `updateRows` re-paths every row it touches
 * from the root, so a nested row would leave its expanded parent. Nested rows,
 * and rows the grid's virtual buffer has dropped, are patched in the
 * data-source cache, which serves their next load.
 *
 * Partial data is accepted so one row whose document was evicted (trash,
 * restore, sign outside Aline) does not make the whole entry unreadable and
 * silence updates for every other row.
 */
export function useReportRowsCacheSync({
  apiRef,
  dataSourceCache,
  watchedRowsVariables,
}: {
  apiRef: React.RefObject<ReportRowsGridApi | null>;
  dataSourceCache: Pick<ReportDataSourceCache, 'updateCachedRows'>;
  watchedRowsVariables: readonly WatchedReportRowsVariables[];
}): void {
  const client = useApolloClient();
  /**
   * The cached row object each grid row was last patched from, per watched entry. Apollo keeps
   * the same object for a row whose data did not change, so a cache write patches only the rows
   * it touched. If Apollo hands back a new object anyway, the row is just patched again. Kept in
   * a ref so a watcher restarted by a tab switch does not repatch every row.
   */
  const appliedRowsByEntryRef = useRef(new Map<string, Map<GridRowId, object>>());

  useEffect(() => {
    const appliedRowsByEntry = appliedRowsByEntryRef.current;
    const variablesByEntryKey = new Map(
      watchedRowsVariables.map(variables => [JSON.stringify(variables), variables])
    );
    for (const entryKey of [...appliedRowsByEntry.keys()]) {
      if (!variablesByEntryKey.has(entryKey)) appliedRowsByEntry.delete(entryKey);
    }

    const pendingRowsById = new Map<GridRowId, PendingRowUpdate>();
    const flushPendingRows = throttle(
      () => {
        const pendingUpdates = [...pendingRowsById.values()];
        pendingRowsById.clear();
        const api = apiRef.current;
        if (!pendingUpdates.length || !api) return;
        const rows = pendingUpdates.map(update => update.gridRow);
        const rootRows = rows.filter(row => api.getRowNode(row.id)?.depth === 0);
        if (rootRows.length) api.updateRows(rootRows);
        dataSourceCache.updateCachedRows(rows);
        for (const { apolloRow, appliedRows, gridRow } of pendingUpdates) {
          appliedRows.set(gridRow.id, apolloRow);
        }
      },
      1000,
      { leading: true, trailing: true }
    );

    const subscriptions = [...variablesByEntryKey].map(([entryKey, variables]) => {
      const appliedRows = appliedRowsByEntry.get(entryKey) ?? new Map<GridRowId, object>();
      appliedRowsByEntry.set(entryKey, appliedRows);
      return client
        .watchQuery<Apollo.GetReportRowsQuery, Apollo.GetReportRowsQueryVariables>({
          fetchPolicy: 'cache-only',
          query: Apollo.GetReportRowsDocument,
          returnPartialData: true,
          variables,
        })
        .subscribe({
          error: error => {
            console.error('Error watching report rows query:', error);
            Sentry.captureException(error);
          },
          next: result => {
            if (!apiRef.current) return;
            const apolloRows = result.data?.report?.reportRows?.rows ?? [];
            const emittedRowIds = new Set<GridRowId>();
            for (const apolloRow of apolloRows) {
              if (!apolloRow?.id) continue;
              emittedRowIds.add(apolloRow.id);
              if (appliedRows.get(apolloRow.id) === apolloRow) continue;
              const gridRow = getGridRowUpdate({
                apolloRow,
                cache: client.cache,
                isPartial: result.dataState === 'partial',
              });
              if (gridRow) pendingRowsById.set(gridRow.id, { apolloRow, appliedRows, gridRow });
            }
            // A row that left the entry (trash, restore) must not keep its cached row alive.
            for (const rowId of [...appliedRows.keys()]) {
              if (!emittedRowIds.has(rowId)) appliedRows.delete(rowId);
            }
            if (pendingRowsById.size) flushPendingRows();
          },
        });
    });

    return () => {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      flushPendingRows.cancel();
    };
  }, [apiRef, client, dataSourceCache, watchedRowsVariables]);
}

/** The grid patch for one cached row, or null when the cached row is incomplete. */
function getGridRowUpdate({
  apolloRow,
  cache,
  isPartial,
}: {
  apolloRow: CachedReportRow;
  cache: ApolloCache;
  isPartial: boolean;
}): GridValidRowModel | null {
  if (!apolloRow.id) return null;

  switch (apolloRow.__typename) {
    case 'GroupHeaderRow':
      return {
        __typename: 'GroupHeaderRow',
        childRowCount: apolloRow.childRowCount ?? null,
        documentCount: apolloRow.documentCount ?? 0,
        groupFieldId: apolloRow.groupFieldId,
        groupKey: apolloRow.groupKey ?? '',
        id: apolloRow.id,
        parentTreeRowId: apolloRow.parentTreeRowId ?? undefined,
      } satisfies DeepPartial<GroupHeaderRow>;
    case 'OrphanRelationshipRow':
      return {
        __typename: 'OrphanRelationshipRow',
        id: apolloRow.id,
        orphanedRelationship: apolloRow.orphanedRelationship,
        orphanedRelationshipDirection: apolloRow.orphanedRelationshipDirection,
        parentTreeRowId: apolloRow.parentTreeRowId ?? undefined,
      } satisfies DeepPartial<OrphanRelationshipRow>;
    case 'UrlRelationshipRow':
      return {
        __typename: 'UrlRelationshipRow',
        childUrl: apolloRow.urlRelationship?.childUrl ?? undefined,
        id: apolloRow.id,
        parentTreeRowId: apolloRow.parentTreeRowId ?? undefined,
        urlRelationship: apolloRow.urlRelationship,
      } satisfies DeepPartial<UrlRelationshipRow>;
    case 'DocumentRow': {
      const doc = apolloRow.document;
      if (!doc?.id) return null;
      if (isPartial && !getIsDocumentReportRowCached(cache, doc.id)) return null;

      const questionJobsByQuestionId = (doc.questionJobs ?? []).reduce<
        Record<UUID, DeepPartial<ReportRowQuestionJob>>
      >((acc, job) => {
        if (job?.question?.id) acc[job.question.id] = job;
        return acc;
      }, {});

      return {
        ...doc,
        childRowCount: apolloRow.childRowCount ?? 0,
        documentId: apolloRow.documentId,
        id: apolloRow.id,
        parentTreeRowId: apolloRow.parentTreeRowId ?? undefined,
        questionJobsByQuestionId,
        relationship: apolloRow.relationship ?? null,
        relationshipDirection: apolloRow.relationshipDirection ?? null,
      } satisfies DeepPartial<ReportGridRow>;
    }
    case undefined:
      return null;
  }
}

// A partial read leaves an evicted nested entity as `{}`, which must not overwrite a grid cell.
function getIsDocumentReportRowCached(cache: ApolloCache, documentId: string): boolean {
  const document = cache.readFragment<Apollo.DocumentReportRowFragment>({
    fragment: Apollo.DocumentReportRowFragmentDoc,
    fragmentName: 'DocumentReportRow',
    id: cache.identify({ __typename: 'Document', id: documentId }),
  });
  return Boolean(document);
}
